import React, {FC, useCallback} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  AiKnowledgeSourceScrapingJobModel,
  KnowledgeBaseAiSourceStatusesEnum,
} from '../../../../../libs/shared/core/src/models/api/jobModel';
import {isJobFailed, isJobRunning} from '../../../../../libs/shared/core/src/models/helpers/jobHelpers';
import {useAppQuery} from '../../../../../libs/shared/core/src/services/query/useAppQuery';
import {jobDeletedAction} from '../../../../../libs/shared/core/src/store/data/jobs/commonJobsActions';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {aiKnowledgeSourcesQueries} from '../../../../settings/aiKnowledgeSources/queries/aIKnowledgeSourcesQueries';

/*
 * Props.
 */

interface AiScrapingJobBannerProps {
  job: AiKnowledgeSourceScrapingJobModel;
}

/*
 * Component.
 */

export const AiScrapingJobBanner: FC<AiScrapingJobBannerProps> = ({job}) => {
  const theme = useUserTheme();
  const syncDataForId = useAppQuery(aiKnowledgeSourcesQueries.fetchById(job.sourceId));

  const displayName = (() => {
    if (syncDataForId.data?.name) {
      return <>{syncDataForId.data?.name}</>;
    }

    if (syncDataForId.data?.domain) {
      return <>{syncDataForId.data.domain}</>;
    }

    return <Trans id="banner-ai-knowledge-source-import-name-unknown">External knowledge source</Trans>;
  })();
  const dispatch = useAppDispatch();

  const onDismissClick = useCallback(() => {
    dispatch(jobDeletedAction(job.id));
  }, [dispatch, job.id]);

  if (syncDataForId.isLoading) {
    return null;
  }
  if (isJobFailed(job)) {
    return (
      <Banner
        bannerType={BannerTypesEnum.MULTILINE}
        priority={BannerPrioritiesEnum.INFORMATION}
        color={theme.palette.red.shade40}
      >
        <BannerIcon iconName="warningFilledSmall" />
        <BannerCloseAction onClick={onDismissClick} />
        <BannerContent>
          <BannerContentTitle>
            <Trans id="banner-ai-knowledge-source-import-failed-title">3rd-party import failed</Trans>
          </BannerContentTitle>
          <Trans id="banner-ai-knowledge-source-import-failed-description">
            We failed to import {displayName}. Please check the settings and try again.
          </Trans>
        </BannerContent>
      </Banner>
    );
  }
  if (!isJobRunning(job)) {
    return renderSuccessBanner(job, theme, onDismissClick, displayName);
  }
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerIcon shouldRotate iconName="repeatCircle" />
      <BannerText>
        <Trans id="banner-ai-knowledge-source-importing-text">Importing {displayName}...</Trans>
      </BannerText>
    </Banner>
  );
};

/*
 * Helpers.
 */

function renderSuccessBanner(
  job: AiKnowledgeSourceScrapingJobModel,
  theme: UserTheme,
  onDismissClick: () => void,
  displayName?: React.ReactNode,
) {
  switch (job.crawledFinishedReason) {
    case KnowledgeBaseAiSourceStatusesEnum.UNSUPPORTED_LANGUAGE:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          priority={BannerPrioritiesEnum.ERROR}
          color={theme.palette.red.shade40}
        >
          <BannerIcon iconName="warningFilledSmall" />
          <BannerCloseAction onClick={onDismissClick} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="banner-ai-knowledge-source-unsupported-language-title">Unsupported language</Trans>
            </BannerContentTitle>
            <Trans id="banner-ai-knowledge-source-unsupported-language-description">
              {displayName} has content in a language we do not support.
            </Trans>
          </BannerContent>
        </Banner>
      );
    case KnowledgeBaseAiSourceStatusesEnum.MAX_CREDITS_USED:
    case KnowledgeBaseAiSourceStatusesEnum.MAX_PAGES_CRAWLED:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.palette.orange.shade40}
        >
          <BannerIcon iconName="warningFilledSmall" />
          <BannerCloseAction onClick={onDismissClick} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="banner-ai-knowledge-source-partially-imported-title">Partially imported</Trans>
            </BannerContentTitle>
            <Trans id="banner-ai-knowledge-source-partially-imported-description">
              {displayName} was only partially imported as we have hit page limits.
            </Trans>
          </BannerContent>
        </Banner>
      );
    case KnowledgeBaseAiSourceStatusesEnum.NO_MORE_URLS:
    default: {
      if (job.totalPagesIngested === 0) {
        return (
          <Banner
            bannerType={BannerTypesEnum.MULTILINE}
            priority={BannerPrioritiesEnum.INFORMATION}
            color={theme.palette.red.shade40}
          >
            <BannerIcon iconName="warningFilledSmall" />
            <BannerCloseAction onClick={onDismissClick} />
            <BannerContent>
              <BannerContentTitle>
                <Trans id="banner-ai-knowledge-source-no-content-title">No content imported.</Trans>
              </BannerContentTitle>
              <Trans id="banner-ai-knowledge-source-no-content-description">
                We failed to find valid content on {displayName}. Note that only text content is supported
                today.
              </Trans>
            </BannerContent>
          </Banner>
        );
      }
      return (
        <Banner
          bannerType={BannerTypesEnum.INLINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.palette.green.shade40}
        >
          <BannerIcon iconName="checkmarkCircleSmall" />
          <BannerCloseAction onClick={onDismissClick} />
          <BannerText>
            <Trans id="banner-ai-knowledge-source-imported-text">Imported {displayName}</Trans>
          </BannerText>
        </Banner>
      );
    }
  }
}
